import { useEffect, useState, useCallback } from "react";
import { Button, Loader } from "@/common/components/ui";
import { CommonSelect, DatePickerInput } from "@/common/components/features";
import NomineeDetailsService from "@/services/NPS/Stepper/APIs/nomineeDetailsService";
import { NomineeDetailsPayload } from "@/utils/types/NPS/Stepper/NomineeDetails";
import { Registration } from "@/utils/types/NPS/Stepper/GetRegistrationDetils";
import { Trash, AlertTriangle } from "lucide-react";

const NomineeDetails: React.FC<{
  ackId: string;
  nextStep: string;
  initialDetails: any;
  setNextStep: (nextStep: string) => void;
  setInitialDetails: (initialDetails: any) => void;
  onNext: () => void;
  onPrev: () => void;
  registrationDetails?: Registration | null;
}> = ({
  ackId,
  onNext,
  setNextStep,
  setInitialDetails,
  onPrev,
  registrationDetails,
}) => {
  const [nominees, setNominees] = useState<any[]>([
    {
      firstName: "",
      middleName: "",
      lastName: "",
      relationship: "",
      majorMinor: "",
      dob: "",
      age: "0",
      percentage: "",
      guardianFirstName: "",
    },
  ]);

  const [errors, setErrors] = useState<Record<number, Record<string, string>>>(
    {}
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [nomineeToDelete, setNomineeToDelete] = useState<number | null>(null);
  const [totalPercentageError, setTotalPercentageError] = useState("");

  useEffect(() => {
    if (
      registrationDetails?.nominationDtls &&
      registrationDetails?.nominationDtls?.length > 0
    ) {
      setNominees(
        registrationDetails?.nominationDtls?.map((item: any) => ({
          firstName: item.firstName,
          middleName: item.middleName,
          lastName: item.lastName,
          relationship: item.relationship,
          majorMinor: item.majorMinorFlag,
          dob: item.dateOfBirth,
          age: item.nomineeAge,
          percentage: item.percentageShare,
          guardianFirstName: item.guardianFirstName,
        }))
      );
    }
  }, [registrationDetails]);

  const handleNomineeChange = (index: number, key: string, value: string) => {
    if (key === "firstName" || key === "middleName" || key === "lastName") {
      value = value.toUpperCase();
    }

    const updatedNominees = [...nominees];
    updatedNominees[index][key] = value;

    // ✅ AUTO-CALCULATE AGE & MAJOR/MINOR WHEN DOB CHANGES
    if (key === "dob") {
      if (!value) {
        updatedNominees[index].age = "";
        updatedNominees[index].majorMinor = "";
        setNominees(updatedNominees);
        return;
      }
      const [day, month, year] = value.split("/");
      const dobDate = new Date(`${year}-${month}-${day}`);

      if (isNaN(dobDate.getTime())) {
        updatedNominees[index].age = "";
        updatedNominees[index].majorMinor = "";
        setNominees(updatedNominees);
        return;
      }

      const today = new Date();
      let calculatedAge = today.getFullYear() - dobDate.getFullYear();
      const monthDiff = today.getMonth() - dobDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < dobDate.getDate())
      ) {
        calculatedAge--;
      }

      updatedNominees[index].age = calculatedAge.toString();

      // ✅ AUTO-SET MAJOR/MINOR
      if (calculatedAge >= 18) {
        updatedNominees[index].majorMinor = "N"; // Major
      } else if (calculatedAge >= 0) {
        updatedNominees[index].majorMinor = "Y"; // Minor
      }
    }

    setNominees(updatedNominees);

    // ✅ Existing error removal logic
    if (errors[index]?.[key]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index][key];
        if (Object.keys(newErrors[index]).length === 0) {
          delete newErrors[index];
        }
        return newErrors;
      });
    }

    // ✅ Existing percentage validation logic remains same
    if (key === "percentage") {
      const totalPercentage = updatedNominees.reduce((sum, nominee) => {
        const percentage = Number(nominee.percentage) || 0;
        return sum + percentage;
      }, 0);

      if (totalPercentage > 100) {
        setTotalPercentageError(
          "Nominee percentage not more than 100 (Please enter all Nominee percentage is 100)"
        );
      } else {
        setTotalPercentageError("");
      }

      setErrors(prev => {
        const newErrors = { ...prev };
        const percentage = Number(value);
        if (
          !value ||
          isNaN(percentage) ||
          percentage <= 0 ||
          percentage > 100
        ) {
          if (!newErrors[index]) {
            newErrors[index] = {};
          }
          newErrors[index].percentage =
            "Nominee Percentage must be a valid number between 0 and 100";
        } else {
          if (newErrors[index]?.percentage) {
            delete newErrors[index].percentage;
            if (Object.keys(newErrors[index]).length === 0) {
              delete newErrors[index];
            }
          }
        }
        return newErrors;
      });
    }
  };

  const addNominee = () => {
    if (nominees.length > 3) {
      alert("You can not add more than 3 nominee");
      return;
    }
    setNominees([
      ...nominees,
      {
        firstName: "",
        middleName: "",
        lastName: "",
        relationship: "",
        majorMinor: "",
        dob: "",
        age: "0",
        percentage: "",
        guardianFirstName: "",
      },
    ]);
  };

  const relationshipOptions = [
    { label: "Mother", value: "01" },
    { label: "Father", value: "02" },
    { label: "Spouse", value: "03" },
    { label: "Son", value: "04" },
    { label: "Daughter", value: "05" },
    { label: "Mother in Law", value: "06" },
    { label: "Father in Law", value: "07" },
    { label: "Daughter in Law", value: "08" },
    { label: "Grandson", value: "09" },
    { label: "Granddaughter", value: "10" },
    { label: "Other", value: "99" },
  ];

  const majorMinorOptions = [
    { label: "Major", value: "N" },
    { label: "Minor", value: "Y" },
  ];

  const validateNominee = (nominee: any) => {
    const newErrors: Record<string, string> = {};
    if (!nominee.firstName.trim()) {
      newErrors.firstName = "First Name is required";
    }
    if (!nominee.relationship) {
      newErrors.relationship = "Relationship is required";
    }
    // if (!nominee.majorMinor) {
    //   newErrors.majorMinor = "Major/Minor is required";
    // }
    if (!nominee.dob) {
      newErrors.dob = "Date of Birth is required";
    }
    if (nominee.majorMinor === "Y" && !nominee.guardianFirstName) {
      newErrors.guardianFirstName = "Guardian First Name is required";
    }
    const age = Number(nominee.age);
    if (!nominee.age || isNaN(age) || age < 0) {
      newErrors.age =
        "Nominee Age (Year) must be a valid number greater than 0";
    }
    const percentage = Number(nominee.percentage);
    if (
      !nominee.percentage ||
      isNaN(percentage) ||
      percentage <= 0 ||
      percentage > 100
    ) {
      newErrors.percentage =
        "Nominee Percentage must be a valid number between 0 and 100";
    }
    return newErrors;
  };

  const handleChange = async () => {
    // Validate all nominees

    const validationErrors = nominees.reduce(
      (acc, nominee, index) => {
        const nomineeErrors = validateNominee(nominee);
        if (Object.keys(nomineeErrors).length > 0) {
          acc[index] = nomineeErrors;
        }
        return acc;
      },
      {} as Record<number, Record<string, string>>
    );

    // Calculate total percentage
    const totalPercentage = nominees.reduce((sum, nominee) => {
      const percentage = Number(nominee.percentage) || 0;
      return sum + percentage;
    }, 0);

    // Check if total percentage is not exactly 100
    let percentageError = "";
    if (totalPercentage > 100) {
      percentageError =
        "Nominee percentage not more than 100 (Please enter all Nominee percentage is 100)";
      setTotalPercentageError(percentageError);
    } else if (totalPercentage < 100) {
      percentageError =
        "Total nominee percentage must be exactly 100 (Please enter all Nominee percentage is 100)";
      setTotalPercentageError(percentageError);
    } else {
      setTotalPercentageError("");
    }

    setErrors(validationErrors);

    // Check if any errors exist (including total percentage error)
    const hasValidationErrors = Object.keys(validationErrors).some(
      key => Object.keys(validationErrors[Number(key)]).length > 0
    );
    const hasPercentageError = totalPercentage !== 100;

    // Don't proceed if there are any validation errors or percentage error
    if (hasValidationErrors || hasPercentageError) {
      return; // Don't call API if there are validation errors
    }
    // Build the payload to pass to onNext
    const payload = {
      tier1: nominees.map(nominee => ({
        firstName: nominee.firstName,
        lastName: nominee.lastName,
        dateOfBirth: nominee.dob, // Assuming dob is in a suitable format
        relationship: nominee.relationship,
        majorMinorFlag: nominee.majorMinor,
        percentageShare: Number(nominee.percentage),
        nomineeAge: Number(nominee.age),
        guardianFirstName:
          nominee.majorMinor === "Y" ? nominee.guardianFirstName : "",
      })),
    };

    // API call to create personal details
    setIsLoading(true); // Set loading state to true before the API call
    try {
      const response = await NomineeDetailsService.createNomineeDetails(
        ackId,
        payload as NomineeDetailsPayload,
        "nomination"
      );
      if (response.success) {
        // Assuming the response contains next_step and initialDetails
        console.log("✅ Registration started:", response.registration);
        setNextStep(response?.registration?.next_step); // Set next step
        setInitialDetails(response?.registration?.initialDetails);
        onNext(); // Proceed to next step after successful API response
      }
    } catch (error) {
      console.error("❌ Registration failed:", error);
    } finally {
      setIsLoading(false); // Set loading state to false after the API call completes
    }
  };

  const deleteNominee = (index: number) => {
    if (nominees.length === 1) {
      alert("At least one nominee is required.");
      return;
    }
    setNomineeToDelete(index);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (nomineeToDelete !== null) {
      const updatedNominees = nominees.filter((_, i) => i !== nomineeToDelete);
      setNominees(updatedNominees);
      setShowDeleteModal(false);
      setNomineeToDelete(null);
    }
  };

  const handleCancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setNomineeToDelete(null);
  }, []);

  // Close modal on scroll
  useEffect(() => {
    if (!showDeleteModal) {
      return;
    }

    const handleScroll = () => {
      handleCancelDelete();
    };

    // Listen to scroll events on window and document
    window.addEventListener("scroll", handleScroll, true);
    document.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [showDeleteModal, handleCancelDelete]);

  return (
    <>
      {isLoading && <Loader message="Submitting your nominee details..." />}
      <div className="space-y-10 rounded-2xl bg-white py-10">
        <h2 className="mb-4 font-sans text-[26px] font-semibold text-[#66A0CD]">
          Nominee Details
        </h2>

        <section>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            <div className="flex flex-col items-end gap-2">
              <Button
                onClick={addNominee}
                disabled={nominees.length >= 3}
                className={`h-[50px] rounded-md px-6 py-2 font-sans text-[18px] font-semibold text-white transition hover:opacity-95 ${
                  nominees.length >= 3
                    ? "cursor-not-allowed bg-gray-400 opacity-60"
                    : "bg-gradient-to-r from-[#127B8D] to-[#10B981]"
                }`}
              >
                Add Nominee
              </Button>
              {nominees.length >= 3 && (
                <p className="text-sm text-red-500">
                  You can not add more than 3 nominee
                </p>
              )}
            </div>
          </div>

          {nominees?.map((nominee, index) => (
            <div key={nominee?.id} className="relative mb-8 space-y-6 pb-6">
              {/* Total Percentage Error - Always show above Nominee Details 1 */}
              {totalPercentageError && index === 0 && (
                <p className="mb-1 text-sm text-red-500">
                  {totalPercentageError}
                </p>
              )}

              <div className="flex items-center justify-between">
                <h4 className="font-sans text-[20px] font-bold text-[#2F363F]">
                  Nominee Details {index + 1}
                </h4>

                {/* ❌ Floating Delete Icon */}
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => deleteNominee(index)}
                    className="rounded-full p-2 text-red-600 hover:bg-red-100"
                  >
                    <Trash size={24} />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
                {/* First Name */}
                <div className="flex flex-col">
                  <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={nominee?.firstName}
                    onChange={e =>
                      handleNomineeChange(index, "firstName", e.target.value)
                    }
                    className={`rounded-md border border-[#DFE7EC] px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD] ${errors[index]?.firstName ? "border-red-500" : ""}`}
                  />
                  {errors[index]?.firstName && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors[index]?.firstName}
                    </p>
                  )}
                </div>

                {/* Middle Name */}
                <div className="flex flex-col">
                  <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
                    Middle Name
                  </label>
                  <input
                    type="text"
                    placeholder="Middle Name"
                    value={nominee?.middleName}
                    onChange={e =>
                      handleNomineeChange(index, "middleName", e.target.value)
                    }
                    className="rounded-md border border-[#DFE7EC] px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD]"
                  />
                </div>

                {/* Last Name */}
                <div className="flex flex-col">
                  <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
                    Last Name
                  </label>
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={nominee?.lastName}
                    onChange={e =>
                      handleNomineeChange(index, "lastName", e.target.value)
                    }
                    className="rounded-md border border-[#DFE7EC] px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD]"
                  />
                </div>

                {/* Relationship */}
                <CommonSelect
                  label="Relationship"
                  placeholder="Relationship"
                  options={relationshipOptions}
                  value={nominee?.relationship}
                  onChange={val =>
                    handleNomineeChange(index, "relationship", val)
                  }
                  required
                  errorMessage={errors[index]?.relationship}
                />

                {/* Major/Minor */}
                <CommonSelect
                  label="Major/Minor"
                  placeholder="Major/Minor"
                  disabled
                  options={majorMinorOptions}
                  value={nominee?.majorMinor}
                  onChange={val =>
                    handleNomineeChange(index, "majorMinor", val)
                  }
                  errorMessage={errors[index]?.majorMinor}
                />

                {/* Date of Birth */}
                <div className="flex flex-col">
                  <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <DatePickerInput
                    value={nominee?.dob}
                    onChange={val => handleNomineeChange(index, "dob", val)}
                    error={errors[index]?.dob}
                  />
                </div>

                {/* Age */}
                <div className="flex flex-col">
                  <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
                    Nominee Age (Year)
                  </label>
                  <input
                    type="text"
                    placeholder="Nominee Age (Year)"
                    value={nominee?.age}
                    disabled
                    onChange={e =>
                      handleNomineeChange(index, "age", e.target.value)
                    }
                    className={`rounded-md border border-[#DFE7EC] px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD] ${errors[index]?.age ? "border-red-500" : ""} disabled:cursor-not-allowed disabled:bg-[rgba(112,112,112,0.3)] disabled:text-[#2F363F] disabled:placeholder-[#2F363F]`}
                  />
                  {errors[index]?.age && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors[index]?.age}
                    </p>
                  )}
                </div>

                {/* Percentage */}
                <div className="flex flex-col">
                  <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
                    Nominee Percentage <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Nominee Percentage"
                    value={nominee?.percentage}
                    onChange={e =>
                      handleNomineeChange(index, "percentage", e.target.value)
                    }
                    className={`rounded-md border border-[#DFE7EC] px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD] ${errors[index]?.percentage ? "border-red-500" : ""}`}
                  />
                  {errors[index]?.percentage && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors[index]?.percentage}
                    </p>
                  )}
                </div>
                {nominee?.majorMinor === "Y" && (
                  <div className="flex flex-col">
                    <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
                      Guardian First Name<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Guardian First Name"
                      value={nominee?.guardianFirstName}
                      onChange={e =>
                        handleNomineeChange(
                          index,
                          "guardianFirstName",
                          e.target.value
                        )
                      }
                      className={`rounded-md border border-[#DFE7EC] px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD] ${errors[index]?.percentage ? "border-red-500" : ""}`}
                    />
                    {errors[index]?.guardianFirstName && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors[index]?.guardianFirstName}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </section>

        <div className="mt-6 flex justify-center gap-3 sm:justify-end md:gap-5">
          <Button
            type="button"
            onClick={() => {
              onPrev();
            }}
            className="h-[50px] rounded-md bg-[#F5F5F5] px-8 py-2 font-sans text-[18px] font-semibold text-[#2F363F] hover:bg-[#f5f5f5] hover:opacity-95"
          >
            Back
          </Button>
          <Button
            type="submit"
            onClick={handleChange}
            className="h-[50px] rounded-md bg-gradient-to-r from-[#127B8D] to-[#10B981] px-6 py-2 font-sans text-[18px] font-semibold text-white transition hover:opacity-95"
          >
            Continue
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <>
          {/* Blurred Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
            onClick={handleCancelDelete}
          />
          {/* Centered Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-start gap-3">
                {/* Warning Icon */}
                <div className="flex-shrink-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500">
                    <AlertTriangle className="h-5 w-5 text-white" />
                  </div>
                </div>

                {/* Message and Buttons */}
                <div className="flex-1">
                  <p className="mb-4 font-sans text-[16px] font-semibold text-[#2F363F]">
                    Are you sure you want to delete this nominee?
                  </p>

                  {/* Buttons */}
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={handleCancelDelete}
                      className="rounded-md border border-gray-300 bg-white px-5 py-2 font-sans text-[14px] font-semibold text-[#2F363F] transition hover:bg-gray-50"
                    >
                      No
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmDelete}
                      className="rounded-md bg-gradient-to-r from-[#127B8D] to-[#10B981] px-5 py-2 font-sans text-[14px] font-semibold text-white transition hover:opacity-90"
                    >
                      Yes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default NomineeDetails;
